import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Search, Share2, Plus, Heart, MessageCircle } from 'lucide-react';

// Mock data for screenshots
const mockChildren = [
  { id: '1', name: 'Emma', birthdate: '2018-03-15', age: 6 },
  { id: '2', name: 'Oliver', birthdate: '2020-07-22', age: 4 },
  { id: '3', name: 'Sophia', birthdate: '2016-11-08', age: 8 }
];

const mockQuotes = [
  {
    id: '1',
    content: 'Mommy, why do grown-ups drink coffee? Is it because they forgot how to be awake?',
    child: 'Emma',
    age: 6,
    date: '2024-09-10',
    context: 'At breakfast',
    likes: 12
  },
  {
    id: '2',
    content: 'I think vegetables are just fruits that gave up on being sweet.',
    child: 'Emma',
    age: 6,
    date: '2024-09-05',
    context: 'Dinner time',
    likes: 8
  },
  {
    id: '3',
    content: 'Daddy, when I grow up I want to be a dinosaur scientist... or a dinosaur!',
    child: 'Oliver',
    age: 4,
    date: '2024-09-12',
    context: 'Playing with toys',
    likes: 15
  },
  {
    id: '4',
    content: 'Why do we have to clean up? The toys like being on the floor!',
    child: 'Oliver',
    age: 4,
    date: '2024-09-08',
    context: 'Cleanup time',
    likes: 6
  },
  {
    id: '5',
    content: 'I think adults just pretend to know what they\'re doing. We\'re all just winging it!',
    child: 'Sophia',
    age: 8,
    date: '2024-09-03',
    context: 'Philosophical moment',
    likes: 23
  },
  {
    id: '6',
    content: 'Why do they call it rush hour when nobody\'s moving?',
    child: 'Sophia',
    age: 8,
    date: '2024-08-25',
    context: 'Stuck in traffic',
    likes: 18
  }
];

type DemoView = 'home' | 'add' | 'timeline' | 'share' | 'search';

const ScreenshotDemo = () => {
  const [currentView, setCurrentView] = useState<DemoView>('home');

  // Screenshot 1: Home Feed
  const HomeView = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">KidQuotes</h1>
        <Button size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Quote
        </Button>
      </div>
      
      <div className="space-y-4">
        {mockQuotes.slice(0, 4).map((quote) => (
          <Card key={quote.id} className="w-full">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold">
                    {quote.child[0]}
                  </div>
                  <div>
                    <p className="font-semibold">{quote.child}</p>
                    <p className="text-sm text-muted-foreground">Age {quote.age} • {quote.date}</p>
                  </div>
                </div>
                <Badge variant="secondary">{quote.context}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-lg mb-3">"{quote.content}"</p>
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Heart className="h-4 w-4" />
                  <span>{quote.likes}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <MessageCircle className="h-4 w-4" />
                  <span>Share</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  // Screenshot 2: Add Quote
  const AddView = () => (
    <div className="space-y-6">
      <div className="flex items-center mb-6">
        <Button variant="ghost" size="sm" className="mr-3">←</Button>
        <h1 className="text-2xl font-bold">Add New Quote</h1>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Capture a Moment</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Child</label>
            <select className="w-full p-2 border rounded-md">
              <option>Emma (6 years old)</option>
              <option>Oliver (4 years old)</option>
              <option>Sophia (8 years old)</option>
            </select>
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Quote</label>
            <Textarea 
              placeholder="What did they say?"
              value="Can we get a pet dragon? I promise I'll walk it every day!"
              className="min-h-[100px]"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Context (Optional)</label>
            <Input placeholder="Where were you? What was happening?" value="At the pet store" />
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Date</label>
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <Input type="date" value="2024-09-15" />
            </div>
          </div>
          
          <Button className="w-full" size="lg">
            Save Quote
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  // Screenshot 3: Timeline View
  const TimelineView = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Timeline</h1>
        <Button variant="outline" size="sm">
          <Calendar className="h-4 w-4 mr-2" />
          Filter
        </Button>
      </div>
      
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold mb-3 text-primary">September 2024</h3>
          <div className="space-y-3 pl-4 border-l-2 border-primary/20">
            {mockQuotes.slice(0, 3).map((quote) => (
              <Card key={quote.id} className="ml-4">
                <CardContent className="pt-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Badge variant="outline">{quote.child}</Badge>
                    <span className="text-sm text-muted-foreground">{quote.date}</span>
                  </div>
                  <p className="text-sm">"{quote.content}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
        
        <div>
          <h3 className="text-lg font-semibold mb-3 text-primary">August 2024</h3>
          <div className="space-y-3 pl-4 border-l-2 border-primary/20">
            {mockQuotes.slice(3, 6).map((quote) => (
              <Card key={quote.id} className="ml-4">
                <CardContent className="pt-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Badge variant="outline">{quote.child}</Badge>
                    <span className="text-sm text-muted-foreground">{quote.date}</span>
                  </div>
                  <p className="text-sm">"{quote.content}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Screenshot 4: Share View
  const ShareView = () => (
    <div className="space-y-6">
      <div className="flex items-center mb-6">
        <Button variant="ghost" size="sm" className="mr-3">←</Button>
        <h1 className="text-2xl font-bold">Share Quote</h1>
      </div>
      
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold">
              E
            </div>
            <div>
              <p className="font-semibold">Emma, age 6</p>
              <p className="text-sm text-muted-foreground">September 10, 2024</p>
            </div>
          </div>
          <p className="text-lg italic mb-4">"Mommy, why do grown-ups drink coffee? Is it because they forgot how to be awake?"</p>
          <Badge variant="secondary">At breakfast</Badge>
        </CardContent>
      </Card>
      
      <div className="space-y-3">
        <h3 className="font-semibold">Share with family</h3>
        <div className="grid grid-cols-2 gap-3">
          <Button variant="outline" className="justify-start">
            <Share2 className="h-4 w-4 mr-2" />
            Messages
          </Button>
          <Button variant="outline" className="justify-start">
            <Share2 className="h-4 w-4 mr-2" />
            Email
          </Button>
          <Button variant="outline" className="justify-start">
            <Share2 className="h-4 w-4 mr-2" />
            Instagram
          </Button>
          <Button variant="outline" className="justify-start">
            <Share2 className="h-4 w-4 mr-2" />
            Facebook
          </Button>
        </div>
        
        <div className="mt-6">
          <h3 className="font-semibold mb-3">Create shareable image</h3>
          <Button className="w-full">
            Generate Quote Card
          </Button>
        </div>
      </div>
    </div>
  );

  // Screenshot 5: Search View
  const SearchView = () => (
    <div className="space-y-4">
      <div className="flex items-center space-x-3 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search quotes..." 
            className="pl-10"
            value="coffee"
          />
        </div>
        <Button variant="outline">Filter</Button>
      </div>
      
      <div className="flex space-x-2 mb-4">
        <Badge variant="secondary">All Kids</Badge>
        <Badge variant="outline">Emma</Badge>
        <Badge variant="outline">Oliver</Badge>
        <Badge variant="outline">Sophia</Badge>
      </div>
      
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">Found 1 quote containing "coffee"</p>
        
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold">
                  E
                </div>
                <div>
                  <p className="font-semibold">Emma</p>
                  <p className="text-sm text-muted-foreground">Age 6 • 2024-09-10</p>
                </div>
              </div>
              <Badge variant="secondary">At breakfast</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-lg">
              "Mommy, why do grown-ups drink <mark className="bg-yellow-200">coffee</mark>? Is it because they forgot how to be awake?"
            </p>
          </CardContent>
        </Card>
        
        <div className="text-center py-8">
          <p className="text-muted-foreground">Try searching for "dinosaur", "vegetables", or "rush hour"</p>
        </div>
      </div>
    </div>
  );

  const renderView = () => {
    switch (currentView) {
      case 'home': return <HomeView />;
      case 'add': return <AddView />;
      case 'timeline': return <TimelineView />;
      case 'share': return <ShareView />;
      case 'search': return <SearchView />;
      default: return <HomeView />;
    }
  };

  return (
    <div className="max-w-md mx-auto min-h-screen bg-background">
      {/* Navigation for switching views */}
      <div className="fixed top-0 left-0 right-0 bg-white border-b z-50 p-2">
        <div className="flex space-x-2 justify-center max-w-md mx-auto">
          <Button 
            size="sm" 
            variant={currentView === 'home' ? 'default' : 'outline'}
            onClick={() => setCurrentView('home')}
          >
            Home
          </Button>
          <Button 
            size="sm" 
            variant={currentView === 'add' ? 'default' : 'outline'}
            onClick={() => setCurrentView('add')}
          >
            Add
          </Button>
          <Button 
            size="sm" 
            variant={currentView === 'timeline' ? 'default' : 'outline'}
            onClick={() => setCurrentView('timeline')}
          >
            Timeline
          </Button>
          <Button 
            size="sm" 
            variant={currentView === 'share' ? 'default' : 'outline'}
            onClick={() => setCurrentView('share')}
          >
            Share
          </Button>
          <Button 
            size="sm" 
            variant={currentView === 'search' ? 'default' : 'outline'}
            onClick={() => setCurrentView('search')}
          >
            Search
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="pt-16 p-4">
        {renderView()}
      </div>
    </div>
  );
};

export default ScreenshotDemo;
